import { ACCOUNT_SIZE, createInitializeAccountInstruction, getMint, TOKEN_PROGRAM_ID } from '@solana/spl-token';
import { Connection, Keypair, PublicKey, SystemProgram, TransactionInstruction, TransactionMessage, VersionedTransaction } from '@solana/web3.js';
import { getVaultOwnerAndNonce } from '../utils/serum';
import { DexInstructions, Market } from '@project-serum/serum';
import BN from 'bn.js';
import { WSOL } from '@raydium-io/raydium-sdk';
import { openbookProgramId } from '../utils/constants';

export const useOpenbook = () => {
    async function getCreateOpenbookMarketTransactions(
        connection: Connection,
        wallet: Keypair,
        baseMint: string | PublicKey,
        quoteMint: string | PublicKey,
        lotSize: number,
        tickSize: number,
        totalEventQueueSize: number,
        totalRequestQueueSize: number,
        totalOrderbookSize: number,
        programID: PublicKey = new PublicKey(openbookProgramId),
    ) {
        console.log('Market wallet', wallet.publicKey.toBase58());
        let baseMintDecimals: number = 6;
        let quoteMintDecimals: number = WSOL.decimals;

        const vaultInstructions: TransactionInstruction[] = [];
        const vaultSigners: Keypair[] = [];

        const marketInstructions: TransactionInstruction[] = [];
        const marketSigners: Keypair[] = [];

        baseMint = new PublicKey(baseMint);
        quoteMint = new PublicKey(quoteMint);

        console.log(`Market deployment: base mint: ${baseMint.toBase58()}, quote mint: ${quoteMint.toBase58()}`);
        console.log(`Market deployment: base mint decimals: ${baseMintDecimals}, quote mint decimals: ${quoteMintDecimals}`);

        const marketAccounts = {
            market: Keypair.generate(),
            requestQueue: Keypair.generate(),
            eventQueue: Keypair.generate(),
            bids: Keypair.generate(),
            asks: Keypair.generate(),
            baseVault: Keypair.generate(),
            quoteVault: Keypair.generate(),
        };

        const [vaultOwner, vaultOwnerNonce] = await getVaultOwnerAndNonce(marketAccounts.market.publicKey, programID);

        vaultInstructions.push(
            ...[
                SystemProgram.createAccount({
                    fromPubkey: wallet.publicKey,
                    newAccountPubkey: marketAccounts.baseVault.publicKey,
                    lamports: await connection.getMinimumBalanceForRentExemption(ACCOUNT_SIZE, 'processed'),
                    space: ACCOUNT_SIZE,
                    programId: TOKEN_PROGRAM_ID,
                }),
                SystemProgram.createAccount({
                    fromPubkey: wallet.publicKey,
                    newAccountPubkey: marketAccounts.quoteVault.publicKey,
                    lamports: await connection.getMinimumBalanceForRentExemption(ACCOUNT_SIZE, 'processed'),
                    space: ACCOUNT_SIZE,
                    programId: TOKEN_PROGRAM_ID,
                }),
                createInitializeAccountInstruction(marketAccounts.baseVault.publicKey, baseMint, vaultOwner),
                createInitializeAccountInstruction(marketAccounts.quoteVault.publicKey, quoteMint, vaultOwner),
            ],
        );
        vaultSigners.push(marketAccounts.baseVault, marketAccounts.quoteVault);

        // tickSize and lotSize here are the 1e^(-x) values, so no check for ><= 0
        const baseLotSize = Math.round(10 ** baseMintDecimals * Math.pow(10, -1 * lotSize));
        const quoteLotSize = Math.round(10 ** quoteMintDecimals * Math.pow(10, -1 * lotSize) * Math.pow(10, -1 * tickSize));

        //priority fees
        marketInstructions.push(
            SystemProgram.createAccount({
                newAccountPubkey: marketAccounts.market.publicKey,
                fromPubkey: wallet.publicKey,
                space: Market.getLayout(programID).span,
                lamports: await connection.getMinimumBalanceForRentExemption(Market.getLayout(programID).span, 'processed'),
                programId: programID,
            }),
        );
        // create request queue
        marketInstructions.push(
            SystemProgram.createAccount({
                newAccountPubkey: marketAccounts.requestQueue.publicKey,
                fromPubkey: wallet.publicKey,
                space: totalRequestQueueSize,
                lamports: await connection.getMinimumBalanceForRentExemption(totalRequestQueueSize, 'processed'),
                programId: programID,
            }),
        );

        // create event queue
        marketInstructions.push(
            SystemProgram.createAccount({
                newAccountPubkey: marketAccounts.eventQueue.publicKey,
                fromPubkey: wallet.publicKey,
                space: totalEventQueueSize,
                lamports: await connection.getMinimumBalanceForRentExemption(totalEventQueueSize, 'processed'),
                programId: programID,
            }),
        );

        const orderBookRentExempt = await connection.getMinimumBalanceForRentExemption(totalOrderbookSize, 'processed');

        // create bids
        marketInstructions.push(
            SystemProgram.createAccount({
                newAccountPubkey: marketAccounts.bids.publicKey,
                fromPubkey: wallet.publicKey,
                space: totalOrderbookSize,
                lamports: orderBookRentExempt,
                programId: programID,
            }),
        );

        // create asks
        marketInstructions.push(
            SystemProgram.createAccount({
                newAccountPubkey: marketAccounts.asks.publicKey,
                fromPubkey: wallet.publicKey,
                space: totalOrderbookSize,
                lamports: orderBookRentExempt,
                programId: programID,
            }),
        );

        marketSigners.push(marketAccounts.market, marketAccounts.requestQueue, marketAccounts.eventQueue, marketAccounts.bids, marketAccounts.asks);
        marketInstructions.push(
            DexInstructions.initializeMarket({
                market: marketAccounts.market.publicKey,
                requestQueue: marketAccounts.requestQueue.publicKey,
                eventQueue: marketAccounts.eventQueue.publicKey,
                bids: marketAccounts.bids.publicKey,
                asks: marketAccounts.asks.publicKey,
                baseVault: marketAccounts.baseVault.publicKey,
                quoteVault: marketAccounts.quoteVault.publicKey,
                baseMint,
                quoteMint,
                baseLotSize: new BN(baseLotSize),
                quoteLotSize: new BN(quoteLotSize),
                feeRateBps: 150, // Unused in v3
                quoteDustThreshold: new BN(500), // Unused in v3
                vaultSignerNonce: vaultOwnerNonce,
                programId: programID,
            }),
        );

        const { blockhash } = await connection.getLatestBlockhash('max');

        const vaultMessages = new TransactionMessage({
            payerKey: wallet.publicKey,
            recentBlockhash: blockhash,
            instructions: vaultInstructions,
        }).compileToV0Message();
        const marketMessages = new TransactionMessage({
            payerKey: wallet.publicKey,
            recentBlockhash: blockhash,
            instructions: marketInstructions,
        }).compileToV0Message();

        const vaultTransaction = new VersionedTransaction(vaultMessages);
        const marketTransaction = new VersionedTransaction(marketMessages);

        vaultTransaction.sign([...vaultSigners, wallet]);
        marketTransaction.sign([...marketSigners, wallet]);

        return { vaultTransaction, marketTransaction, marketId: marketAccounts.market.publicKey.toBase58() };
    }

    return { getCreateOpenbookMarketTransactions };
};
